import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateStaffRequest, Session } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { queryKeys } from '@/shared/api/query-keys.ts'
import * as api from './api.ts'

export function useStaff() {
  return useQuery({
    queryKey: queryKeys.staff,
    queryFn: ({ signal }) => api.fetchStaff(signal),
    staleTime: 30 * 1000,
  })
}

function useStaffMutation<TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.staff }),
  })
}

export const useCreateStaff = () => useStaffMutation((payload: CreateStaffRequest) => api.createStaff(payload))

export const useReissueInvite = () => useStaffMutation((id: string) => api.reissueInvite(id))

export const useSetStaffEnabled = () =>
  useStaffMutation(({ id, enabled }: { id: string; enabled: boolean }) => api.setStaffEnabled(id, enabled))

export function useInvite(token: string) {
  return useQuery({
    queryKey: queryKeys.invite(token),
    queryFn: ({ signal }) => api.fetchInvite(token, signal),
    retry: (attempt, error) => attempt < 2 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
  })
}

export function useAcceptInvite(token: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (password: string) => api.acceptInvite(token, password),
    onSuccess: (session: Session) => {
      queryClient.setQueryData(queryKeys.session, session)
    },
  })
}
